/**
 * File: LoginPage.tsx
 * Path: apps/admin/src/routes/LoginPage.tsx
 * Description: High-density Industrial HUD for authentication. Refactored to remove legacy magic link verification logic.
 */

import React from "react";
import { LoginForm } from "@/modules/auth/components/LoginForm";
import office_desks_webp from "@idk/assets/backgrounds/office-desks.webp";

export const LoginPage: React.FC = () => {
  return (
    <main className="relative min-h-screen w-full overflow-hidden flex items-center font-sans bg-background">
      {/* 1. Environment Layer: Image + Noise + Gradient */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat grayscale-[0.5] scale-105"
          style={{ backgroundImage: `url(${office_desks_webp})` }}
        />

        {/* CSS-based noise texture */}
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Multidimensional Mask */}
        <div className="absolute inset-0 z-10 bg-gradient-to-b lg:bg-gradient-to-r from-background/80 via-background/90 to-background" />
      </div>

      {/* 2. Content Grid */}
      <div className="login-grid relative z-20 container mx-auto px-gutter-md lg:px-section-sm grid grid-cols-1 lg:grid-cols-12 items-center w-full gap-cluster-md lg:gap-cluster-lg py-section-xs">
        {/* Left Section: Hero Branding */}
        <div className="flex flex-col items-center w-full lg:items-start lg:col-span-7 animate-in fade-in slide-in-from-left-8 duration-1000">
          <h1 className="login-heading text-5xl md:text-7xl lg:text-9xl font-black w-full tracking-[calc(-0.05em)] text-foreground uppercase leading-[0.85] text-left md:text-center lg:text-left">
            Admin
            <br />
            <span
              className="text-transparent"
              style={{ WebkitTextStroke: "1px hsl(var(--foreground))" }}
            >
              Console
            </span>
          </h1>

          <span className="block h-[4px] w-full bg-primary mt-stack-lg md:max-w-sm lg:max-w-xl" />

          {/* Technical Metadata */}
          <div className="login-meta mt-stack-lg grid grid-cols-1 sm:grid-cols-2 px-gutter-sm gap-stack-xl w-full md:max-w-sm lg:max-w-xl">
            <div className="space-y-1">
              <p className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                Node Status
              </p>
              <p className="text-xs font-mono text-primary/80">
                01_ENCRYPTED_STABLE
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                Location
              </p>
              <p className="text-xs font-mono text-primary/80">
                CPT_ZA // 33.9249° S
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Form Container */}
        <div className="login-form flex justify-center lg:col-span-5 lg:justify-end animate-in fade-in zoom-in-95 duration-700 delay-300">
          <div className="w-full relative group">
            <div className="relative">
              <LoginForm />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};